import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Paperclip, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import AttachmentPreview from "@/pages/applications/AttachmentPreview";
import { appInboxStatusOptions, appInboxSetAppStatus, appInboxAttachmentBlob } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const _statusOptionsCache = {};   // region → { options: [], groups: [{label, options}] }
export const loadStatusOptions = async () => {
  const key = localStorage.getItem("sf_region") === "AUS" ? "AUS" : "UK";
  if (!_statusOptionsCache[key]) {
    try { _statusOptionsCache[key] = await appInboxStatusOptions(); }
    catch { return { options: [], groups: [] }; }
  }
  return _statusOptionsCache[key];
};

export const INTENT_TONE = {
  "New App":                "bg-green-100 text-green-800 border-green-300",
  "Top Up":                 "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Info Requested":         "bg-blue-100 text-blue-800 border-blue-300",
  "Final Checks":           "bg-indigo-100 text-indigo-800 border-indigo-300",
  "Open Banking":           "bg-cyan-100 text-cyan-800 border-cyan-300",
  "Invoice":                "bg-amber-100 text-amber-800 border-amber-300",
  "Chase/Status Update":    "bg-orange-100 text-orange-800 border-orange-300",
  "Broker Intro":           "bg-purple-100 text-purple-800 border-purple-300",
  "Cancellation/Withdrawn": "bg-red-100 text-red-800 border-red-300",
  "Spam/Promo":             "bg-slate-100 text-slate-600 border-slate-300",
  "Bounce/Auto-Reply":      "bg-slate-100 text-slate-500 border-slate-300",
  "Other":                  "bg-slate-100 text-slate-600 border-slate-300",
};

export const STATUS_TONE = {
  proposed: "bg-amber-50 text-amber-800 border-amber-300",
  failed:   "bg-red-50 text-red-700 border-red-300",
  executed: "bg-green-50 text-green-700 border-green-300",
  rejected: "bg-slate-100 text-slate-500 border-slate-300",
  stale:    "bg-slate-100 text-slate-400 border-slate-200",
};

export const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

export const fmtKb = (b) => (b ? `${Math.round(b / 1024)} KB` : "");

export const fmtUkDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export const isPreviewable = (a) => {
  const t = (a.contentType || "").toLowerCase();
  const n = (a.name || "").toLowerCase();
  return t.includes("pdf") || t.startsWith("image/") || t.startsWith("text/")
    || /\.(pdf|png|jpe?g|gif|webp|txt|csv)$/.test(n);
};

export const downloadAttachment = async (messageId, a, fetchBlob = appInboxAttachmentBlob) => {
  try {
    const blob = await fetchBlob(messageId, a.id);
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url;
    el.download = (a.name || "file").split(" → ").pop();
    el.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Download failed");
  }
};

export function StatusSelect({ value, onChange, testId, className }) {
  const [data, setData] = useState({ options: [], groups: [] });
  useEffect(() => { loadStatusOptions().then(d => setData(d || { options: [], groups: [] })); }, []);
  const opts = data.options || [];
  const groups = data.groups || [];
  return (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)}
      className={className || "h-7 text-[11px] border rounded px-1.5 bg-white max-w-[190px]"}
      data-testid={testId}>
      <option value="">— keep current status —</option>
      {groups.length > 0
        ? groups.map(g => (
            <optgroup key={g.label} label={g.label}>
              {g.options.map(o => <option key={o} value={o}>{o}</option>)}
            </optgroup>
          ))
        : opts.map(o => <option key={o} value={o}>{o}</option>)}
      {value && !opts.includes(value) && <option value={value}>{value}</option>}
    </select>
  );
}

export function ApplySuggestedBtn({ item }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const sug = item.payload?.suggested_status;
  if (!sug || done || sug === (item.result?.status_set || "")) return null;
  return (
    <Button size="sm" variant="outline" className="h-5 text-[9px] px-1.5 shrink-0 text-blue-700 border-blue-300"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await appInboxSetAppStatus(item.payload.item_id, sug);
          toast.success(`${item.payload.sf || "Item"} status set to '${sug}'`);
          setDone(true);
        } catch (e) { toast.error(errorDetailMessage(e, e.message)); }
        finally { setBusy(false); }
      }}
      data-testid={`apply-suggested-${item.queue_id}`}
      title={item.payload?.status_reasoning || ""}>
      {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : `Set '${sug}'`}
    </Button>
  );
}

export function AttachmentPreviewDialog({ messageId, att, onClose, fetchBlob = appInboxAttachmentBlob }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl" aria-describedby={undefined} data-testid="attachment-preview-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2 pr-6">
            <Paperclip className="w-4 h-4 text-slate-500 shrink-0" />
            <span className="truncate">{att.name}</span>
            <span className="text-[10px] text-muted-foreground font-normal shrink-0">{fmtKb(att.size)}</span>
          </DialogTitle>
        </DialogHeader>
        <AttachmentPreview messageId={messageId} att={att} fetchBlob={fetchBlob} />
      </DialogContent>
    </Dialog>
  );
}

export function AttachmentActionIcon({ messageId, att, onPreview, fetchBlob = appInboxAttachmentBlob }) {
  if (isPreviewable(att)) {
    return (
      <button type="button" title="Preview"
        onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); onPreview(att); }}
        className="text-slate-400 hover:text-blue-600 transition shrink-0"
        data-testid={`att-preview-${att.id}`}>
        <Eye className="w-3.5 h-3.5" />
      </button>
    );
  }
  return (
    <button type="button" title="Download"
      onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); downloadAttachment(messageId, att, fetchBlob); }}
      className="text-slate-400 hover:text-blue-600 transition shrink-0"
      data-testid={`att-download-${att.id}`}>
      <Download className="w-3.5 h-3.5" />
    </button>
  );
}
